import AsyncSelect from "react-select/async";
import { isEmpty } from "lodash";
import { useContext, useEffect } from "react";
import { useSelector } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link, useParams } from "react-router-dom";
import { Modal, ModalBody, ModalHeader } from "react-bootstrap";

import SiteTitle from "./../../../helpers/SiteTitle/SiteTitle";
import TableFilter from "../../../components/TableFilter";
import UserAvatar from "../../../components/UserAvatar";
import PaginationComponent from "../../../components/Pagination";
import EmptyData from "../../../components/EmptyData";
import useUploadFile from "../../../hook/useUploadFile";
import ErrorMessage from "../../../components/ErrorMessage";
import Badge from "../../../components/Badge";
import { ProductCard } from "../../Public/ProductsPublic/ProductsPublic";
import { ticketUserContext } from "../../../context/UserContext/TicketUserContext/TicketUserContext";
import {
  assignTicketForAdminValidation,
  createTicketForAdminValidation,
  createTicketValidation,
} from "../../../utils/validation/validation";
import {
  convertRoleToPath,
  convertRoleToText,
  convertTicketTypeToColor,
  convertTicketTypeToText,
  ticketTypeItems,
} from "../../../utils/enum";
import { convertDate } from "../../../utils";
import { areYouSureMessage } from "../../../utils/toastMessage/toastMessage";
import { hostname } from "../../../services/configServices/config";

const TicketsUser = () => {
  const auth = useSelector((state) => state.auth);
  const user = useSelector((state) => state.user);

  const { id } = useParams();

  const { handleUploadFile } = useUploadFile();

  const {
    tickets,
    ticket,
    setTicket,
    status,
    setStatus,
    filter,
    setFilter,
    sort,
    setSort,
    start,
    setStart,
    end,
    setEnd,
    pageCount,
    itemsCount,
    setItemsCount,
    pageNumber,
    setPageNumber,
    setIsShowModal,
    handleSendTicketMessage,
    handleCloseTicket,
    handleGetTickets,
    handleGetTicket,
    handleDeleteTicket,
  } = useContext(ticketUserContext);

  useEffect(() => {
    handleGetTickets();
  }, [start, end, pageNumber, itemsCount, status, sort]);

  useEffect(() => {
    if (!!id) handleGetTicket(id);
    else setTicket({});
  }, [id]);

  return (
    <>
      <SiteTitle title="پشتیبانی" />

      <TableFilter
        setItemsCount={setItemsCount}
        itemsCount={itemsCount}
        setSort={setSort}
        sort={sort}
        setTicketStatus={setStatus}
        ticketStatus={status}
        setStart={setStart}
        start={start}
        setEnd={setEnd}
        end={end}
      />

      <div className="chat-wrapper">
        <div className="chat-sidebar card">
          <div className="chat-sidebar-single active top-profile">
            <div className="img">
              <UserAvatar
                {...(user?.avatar
                  ? { image: user?.avatar }
                  : {
                      icon: "solar:user-broken",
                    })}
              />
            </div>
            <div className="info">
              <h6 className="text-md mb-0">
                {user.firstName} {user.lastName}
              </h6>
              <p className="mb-0">{convertRoleToText(auth.role)}</p>
            </div>
            <div className="action">
              <div className="btn-group">
                <button
                  type="button"
                  className="text-secondary-light text-xl"
                  data-bs-toggle="dropdown"
                  data-bs-display="static"
                  aria-expanded="false"
                >
                  <Icon icon="bi:three-dots" />
                </button>
                <ul className="dropdown-menu dropdown-menu-lg-end border">
                  <li>
                    <Link
                      to={`/${convertRoleToPath(user?.role)}/profile`}
                      className="dropdown-item rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 d-flex align-items-center gap-2"
                    >
                      <Icon icon="solar:user-broken" />
                      حساب کاربری
                    </Link>
                  </li>
                  <li>
                    <Link
                      to={`/${convertRoleToPath(user?.role)}/profile/settings`}
                      className="dropdown-item rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 d-flex align-items-center gap-2"
                    >
                      <Icon icon="solar:settings-broken" />
                      تنظیمات
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <form className="chat-search" onSubmit={handleGetTickets}>
            <input
              type="text"
              autoComplete="off"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="عنوان، کد پیگیری و ..."
            />
            <button className="icon" type="submit">
              <Icon icon="iconoir:search" />
            </button>
          </form>
          {isEmpty(tickets) ? (
            <div className="chat-all-list d-flex justify-content-center align-items-center">
              <div className="w-100 text-center">
                <EmptyData title="درخواستی یافت نشد" />
                <div className="d-flex justify-content-center">
                  <button
                    className="bg-primary-500 rounded px-3 py-10 text-white d-flex justify-content-between align-items-center gap-3"
                    onClick={() => {
                      setIsShowModal(true);
                    }}
                  >
                    <Icon
                      icon="solar:pen-new-round-broken"
                      className="text-xxl"
                    />
                    <span>ایجاد درخواست</span>
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="chat-all-list">
                {tickets.map((item) => (
                  <Link
                    to={`/${convertRoleToPath(auth.role)}/tickets/${item._id}`}
                    className={`chat-sidebar-single ${
                      ticket._id === item._id ? "bg-primary-50" : ""
                    }`}
                    key={item._id}
                  >
                    <div className="img">
                      {auth?.isAdmin ? (
                        <UserAvatar
                          {...(item?.user?.avatar
                            ? { image: item?.user?.avatar }
                            : {
                                icon: "solar:user-broken",
                              })}
                        />
                      ) : (
                        <UserAvatar icon="solar:chat-square-call-broken" />
                      )}
                    </div>
                    <div className="info">
                      <h6 className="text-sm mb-1">
                        {auth?.isAdmin ? (
                          <>
                            {item?.user?.firstName} {item?.user?.lastName}
                          </>
                        ) : (
                          item.title
                        )}
                      </h6>
                      <p className="mb-0 text-xs">
                        {auth?.isAdmin ? (
                          <>
                            <span className="fw-bold">{item.title}</span>
                            <br />
                            {item.trackingCode}
                          </>
                        ) : (
                          <>{item.trackingCode}</>
                        )}
                      </p>
                    </div>
                    <div className="action text-end">
                      <p className="text-neutral-400 text-xs lh-1">
                        {convertDate(item.updatedAt, "DD MMM - HH:mm")}
                      </p>
                      <Badge color={convertTicketTypeToColor(item.status)}>
                        {convertTicketTypeToText(item.status)}
                      </Badge>
                    </div>
                  </Link>
                ))}
              </div>
              <div className="d-flex justify-content-between align-items-end p-10">
                <div>
                  <PaginationComponent
                    pageCount={pageCount}
                    currentPage={pageNumber}
                    setPageNumber={setPageNumber}
                  />
                </div>
                <Icon
                  icon="solar:pen-new-round-broken"
                  className="bg-primary bg-hover-primary-500 text-white rounded-circle display-5 p-6 cursor-pointer"
                  onClick={() => {
                    setIsShowModal(true);
                  }}
                />
              </div>
            </>
          )}
        </div>
        {isEmpty(ticket) ? (
          <div className="chat-main card d-flex justify-content-center align-items-center">
            <div className="w-100">
              <EmptyData title="پیامی انتخاب نشده است" />
            </div>
          </div>
        ) : (
          <div className="chat-main card">
            <div className="chat-sidebar-single">
              <div className="img">
                {auth?.isAdmin ? (
                  <UserAvatar
                    {...(ticket?.user?.avatar
                      ? { image: ticket?.user?.avatar }
                      : {
                          icon: "solar:user-broken",
                        })}
                  />
                ) : (
                  <UserAvatar icon="solar:chat-square-call-broken" />
                )}
              </div>
              <div className="info">
                {auth?.isAdmin ? (
                  <>
                    <h6 className="text-md mb-0">
                      {ticket?.user?.firstName} {ticket?.user?.lastName}
                    </h6>
                    <span className="text-xs">
                      {convertRoleToText(ticket?.user?.role)}
                    </span>
                  </>
                ) : (
                  <>
                    <h6 className="text-md mb-0">{ticket.title}</h6>
                    <span className="text-xs">{ticket.trackingCode}</span>
                  </>
                )}
              </div>
              <div className="action d-inline-flex align-items-center gap-3">
                <Link
                  type="button"
                  className="text-xl text-primary-light"
                  to={`/${convertRoleToPath(auth.role)}/tickets`}
                >
                  <Icon icon="solar:close-circle-broken" />
                </Link>
                <div className="btn-group">
                  <button
                    type="button"
                    className="text-primary-light text-xl"
                    data-bs-toggle="dropdown"
                    data-bs-display="static"
                    aria-expanded="false"
                  >
                    <Icon icon="tabler:dots-vertical" />
                  </button>
                  <ul className="dropdown-menu dropdown-menu-lg-start border">
                    {auth.isAdmin && (
                      <li>
                        <button
                          className="dropdown-item rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 d-flex align-items-center gap-2"
                          type="button"
                          onClick={() =>
                            setTicket((prev) => ({ ...prev, isAssign: true }))
                          }
                        >
                          <Icon icon="solar:users-group-two-rounded-broken" />
                          ویرایش اتصالات
                        </button>
                      </li>
                    )}
                    {ticket.status !== ticketTypeItems[2] && (
                      <li>
                        <button
                          className="dropdown-item rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 d-flex align-items-center gap-2"
                          type="button"
                          onClick={() =>
                            areYouSureMessage(
                              handleCloseTicket,
                              `آیا از بستن درخواست ${ticket.title} مطمئنید؟`
                            )
                          }
                        >
                          <Icon icon="solar:lock-broken" />
                          بستن درخواست
                        </button>
                      </li>
                    )}
                    {auth.isAdmin && (
                      <li>
                        <button
                          className="dropdown-item rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 d-flex align-items-center gap-2"
                          type="button"
                          onClick={() =>
                            areYouSureMessage(
                              handleDeleteTicket,
                              `آیا از حذف درخواست ${ticket.title} مطمئنید؟`
                            )
                          }
                        >
                          <Icon icon="solar:trash-bin-minimalistic-broken" />
                          حذف درخواست
                        </button>
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
            <div className="chat-message-list" dir="ltr">
              {!isEmpty(ticket.messages) &&
                ticket.messages.map((item) => {
                  const isMe = item.user?._id === user._id;
                  return (
                    <div
                      className={`chat-single-message ${
                        isMe ? "right" : "left"
                      }`}
                    >
                      {!isMe && (
                        <>
                          {item.user ? (
                            <UserAvatar
                              {...(item.user?.avatar
                                ? { image: item.user?.avatar }
                                : {
                                    icon: "solar:user-broken",
                                  })}
                              className="avatar-lg object-fit-cover rounded-circle"
                            />
                          ) : (
                            <>
                              {auth?.isAdmin ? (
                                <UserAvatar
                                  {...(ticket?.user?.avatar
                                    ? { image: ticket?.user?.avatar }
                                    : {
                                        icon: "solar:user-broken",
                                      })}
                                />
                              ) : (
                                <UserAvatar
                                  {...(user?.avatar
                                    ? { image: user?.avatar }
                                    : {
                                        icon: "solar:user-broken",
                                      })}
                                  className="avatar-lg object-fit-cover rounded-circle"
                                />
                              )}
                            </>
                          )}
                        </>
                      )}
                      <div className="chat-message-content" dir="rtl">
                        {!isMe && (
                          <div className="info mb-3 d-flex justify-content-between align-items-center text-black">
                            {item.user ? (
                              <>
                                <h6 className="text-md mb-0">
                                  {item.user?.firstName} {item.user?.lastName}
                                </h6>
                                <span className="text-sm">
                                  {convertRoleToText(item?.user?.role)}
                                </span>
                              </>
                            ) : (
                              <>
                                {auth?.isAdmin ? (
                                  <>
                                    <h6 className="text-md mb-0">
                                      {ticket.user?.firstName}{" "}
                                      {ticket.user?.lastName}
                                    </h6>
                                    <span className="text-sm">
                                      {convertRoleToText(ticket?.user?.role)}
                                    </span>
                                  </>
                                ) : (
                                  <>
                                    <h6 className="text-md mb-0">
                                      {user?.firstName} {user?.lastName}
                                    </h6>
                                    <span className="text-muted">
                                      {convertRoleToText(user?.role)}
                                    </span>
                                  </>
                                )}
                              </>
                            )}
                          </div>
                        )}
                        <p className="text-pre-wrap">{item.message}</p>
                        <div className="d-flex justify-content-between align-items-center">
                          <p className="mb-0">
                            {item.file && (
                              <Icon
                                icon="solar:cloud-download-broken"
                                className="cursor-pointer text-2xl"
                                onClick={() =>
                                  window.open(hostname + item.file)
                                }
                              />
                            )}
                          </p>
                          <p className="chat-time mb-0">
                            <span>
                              {convertDate(
                                item.createdAt,
                                "DD MMM YYYY - HH:mm"
                              )}
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
            <form
              className="chat-message-box justify-content-end"
              autoComplete="off"
              onSubmit={handleSendTicketMessage}
            >
              <textarea
                type="text"
                name="message"
                id="message"
                className="form-control"
                placeholder="پیام خود را بنویسید"
              />
              <div className="chat-message-box-action">
                <label htmlFor="attachment" type="button" className="text-xl">
                  <Icon icon="solar:file-broken" />
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png,.gif,.docx,.xlsx,.csv,.pdf,.mp4,.avi;capture=camera"
                    id="attachment"
                    name="attachment"
                    className="d-none"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      handleUploadFile(
                        file,
                        (e) => (document.getElementById(`clip`).value = e),
                        2
                      );
                      document.getElementById(`attachment`).value = "";
                    }}
                  />
                </label>

                <input type="hidden" id="clip" name="clip" />
                <button
                  type="submit"
                  className="btn btn-sm btn-primary-600 radius-8 d-inline-flex align-items-center gap-1"
                >
                  ارسال
                  <Icon icon="f7:paperplane" />
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
      {ticket.product && (
        <div className="mt-3">
          <ProductCard product={ticket.product} />
        </div>
      )}
      <AssignUser />
      <CreateTicket />
    </>
  );
};

export default TicketsUser;

const AssignUser = () => {
  const loader = useSelector((state) => state.loader);

  const { ticket, setTicket, handleGetUsers, handleAssignTicket } =
    useContext(ticketUserContext);

  // Validation
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(assignTicketForAdminValidation),
  });

  useEffect(() => {
    if (ticket.isAssign) {
      reset({
        assign: ticket?.assign?.map((item) => ({
          value: item._id,
          label: `${item.firstName} ${item.lastName} - ${
            item.phoneNumber
          } - ${convertRoleToText(item.role)}`,
        })),
      });
    }
  }, [ticket.isAssign]);

  return (
    <>
      <Modal
        show={ticket.isAssign}
        className="modal-dialog-centered"
        size="md"
        onHide={() => setTicket((prev) => ({ ...prev, isAssign: false }))}
      >
        <ModalHeader closeButton>
          <span className="fs-5 fw-bold">
            ویرایش اتصالات درخواست {ticket.title}
          </span>
        </ModalHeader>
        <ModalBody>
          <form autoComplete="off" onSubmit={handleSubmit(handleAssignTicket)}>
            <div className="mb-20">
              <div>
                <label
                  htmlFor="assign"
                  className="form-label fw-semibold text-primary-light text-sm mb-8"
                >
                  کاربران
                </label>
                <Controller
                  control={control}
                  render={({ field: { value, onChange, onBlur } }) => (
                    <AsyncSelect
                      openMenuOnFocus
                      isMulti
                      cacheOptions
                      className="react-select-container"
                      classNamePrefix="react-select"
                      placeholder="لطفا کاربر را انتخاب نمایید"
                      isLoading={loader}
                      onBlur={onBlur}
                      noOptionsMessage={() => (
                        <p>کاربر ای برای نمایش یافت نشد</p>
                      )}
                      loadingMessage={() => <p>لطفا منتظر بمانید ...</p>}
                      loadOptions={(val, callback) => {
                        handleGetUsers(val, callback);
                      }}
                      defaultOptions
                      value={value}
                      onChange={(e) => onChange(e)}
                      isClearable
                    />
                  )}
                  name="assign"
                />
              </div>

              <ErrorMessage message={errors.assign?.message} />
            </div>

            <button
              type="submit"
              className="btn btn-primary text-sm btn-sm px-12 py-16 w-100 radius-12 mt-20"
            >
              ویرایش درخواست
            </button>
          </form>
        </ModalBody>
      </Modal>
    </>
  );
};

export const CreateTicket = ({ product }) => {
  const auth = useSelector((state) => state.auth);
  const loader = useSelector((state) => state.loader);

  const { handleUploadFile } = useUploadFile();

  const { isShowModal, setIsShowModal, handleGetUsers, handleCreateTicket } =
    useContext(ticketUserContext);

  // Validation
  const {
    control,
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      product,
    },
    resolver: yupResolver(
      auth?.isAdmin ? createTicketForAdminValidation : createTicketValidation
    ),
  });

  useEffect(() => {
    reset();
  }, [isShowModal]);

  return (
    <>
      {product && (
        <div className="alert alert-info bg-info-50 text-info-500 border-info-100 py-1 mt-1 text-sm radius-10 d-flex justify-content-between align-items-center">
          <div className="w-75">
            درصورت داشتن هرگونه ابهام و سوال میتوانید تیکت خود را برای پشتیبانی
            ثبت نمایید
          </div>
          <button
            className="border border-primary-600 bg-primary-50 bg-hover-primary-100 text-primary-600 text-sm radius-8 w-25 text-center py-6"
            onClick={() => setIsShowModal(true)}
          >
            ثبت تیکت پشتیبانی
          </button>
        </div>
      )}
      <Modal
        show={isShowModal}
        className="modal-dialog-centered"
        size="md"
        onHide={() => setIsShowModal(false)}
      >
        <ModalHeader closeButton>
          <span className="fs-5 fw-bold">ایجاد درخواست</span>
        </ModalHeader>
        <ModalBody>
          <form autoComplete="off" onSubmit={handleSubmit(handleCreateTicket)}>
            <div className="row">
              {auth.isAdmin && (
                <div className="col-12">
                  <div className="mb-20">
                    <div>
                      <label
                        htmlFor="user"
                        className="form-label fw-semibold text-primary-light text-sm mb-8"
                      >
                        کاربر
                      </label>
                      <Controller
                        control={control}
                        render={({ field: { onChange, onBlur } }) => (
                          <AsyncSelect
                            openMenuOnFocus
                            cacheOptions
                            className="react-select-container"
                            classNamePrefix="react-select"
                            placeholder="لطفا کاربر را انتخاب نمایید"
                            isLoading={loader}
                            onBlur={onBlur}
                            noOptionsMessage={() => (
                              <p>کاربر ای برای نمایش یافت نشد</p>
                            )}
                            loadingMessage={() => <p>لطفا منتظر بمانید ...</p>}
                            loadOptions={(val, callback) => {
                              handleGetUsers(val, callback);
                            }}
                            defaultOptions
                            onChange={(e) => onChange(e.value)}
                            isClearable
                          />
                        )}
                        name="user"
                      />
                    </div>

                    <ErrorMessage message={errors.user?.message} />
                  </div>
                </div>
              )}
              <div className="col-12">
                <div className="mb-20">
                  <div>
                    <label
                      htmlFor="title"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      عنوان
                    </label>
                    <input
                      type="text"
                      className="form-control radius-8"
                      placeholder="عنوان را وارد نمایید"
                      {...register("title")}
                    />
                  </div>

                  <ErrorMessage message={errors.title?.message} />
                </div>
              </div>
              <div className="col-12">
                <div className="mb-20">
                  <div>
                    <label
                      htmlFor="message"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      پیام
                    </label>
                    <textarea
                      type="text"
                      className="form-control radius-8"
                      placeholder="پیام را وارد نمایید"
                      rows={8}
                      {...register("message")}
                    />
                  </div>

                  <ErrorMessage message={errors.message?.message} />
                </div>
              </div>
              <div className="col-12">
                <div className="mb-20">
                  <div>
                    <label
                      htmlFor="file"
                      className="form-label fw-semibold text-primary-light text-sm mb-8 d-block"
                    >
                      فایل ضمیمه <Badge color="primary">اختیاری</Badge>
                    </label>
                    <label
                      htmlFor="file"
                      className="text-black p-8 border border-info-200 bg-info-50 bg-hover-info-100 radius-12 text-center w-100 shadow text-nowrap text-line-1"
                    >
                      {watch("file") || "انتخاب فایل ضمیمه"}

                      <input
                        type="file"
                        accept=".jpg,.jpeg,.png,.gif,.docx,.xlsx,.csv,.pdf,.mp4,.avi;capture=camera"
                        id="file"
                        className="d-none"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          handleUploadFile(file, (e) => setValue("file", e));
                          document.getElementById(`file`).value = "";
                        }}
                      />
                    </label>
                  </div>

                  <ErrorMessage message={errors.file?.message} />
                </div>
              </div>
            </div>

            <button
              type="submit"
              className="btn btn-primary text-sm btn-sm px-12 py-16 w-100 radius-12 mt-20"
            >
              ایجاد درخواست
            </button>
          </form>
        </ModalBody>
      </Modal>
    </>
  );
};
